import React, { useState, useRef, useEffect } from 'react';
import { 
  FaLightbulb, 
  FaSearch, 
  FaPencilRuler, 
  FaCode, 
  FaRocket, 
  FaUserCheck,
  FaArrowRight,
  FaArrowLeft
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';

const WorkProcess = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { currentTheme } = useTheme();
  const { currentLanguage } = useLanguage();
  
  // Translations
  const translations = {
    ru: {
      title: "Как мы работаем",
      description: "Наш процесс разработки ориентирован на результат и прозрачность на каждом этапе. Мы следуем проверенной методологии, которая обеспечивает высокое качество и эффективность.",
      steps: [
        {
          icon: <FaLightbulb />,
          title: 'Идея и консультация',
          description: 'Обсуждаем ваши идеи, бизнес-цели и требования к проекту. Проводим бесплатную консультацию, определяем задачи и составляем план действий.',
          color: '#00F0FF',
          lightColor: '#1A56DB'
        },
        {
          icon: <FaSearch />,
          title: 'Исследование и аналитика',
          description: 'Анализируем рынок, конкурентов и целевую аудиторию. Определяем ключевые метрики успеха и формируем техническое задание.',
          color: '#33D9E8',
          lightColor: '#3670DB'
        },
        {
          icon: <FaPencilRuler />,
          title: 'Проектирование и дизайн',
          description: 'Разрабатываем прототипы и дизайн-концепции, выстраиваем пользовательские сценарии и создаем уникальный визуальный язык проекта.',
          color: '#66C2D1',
          lightColor: '#5182DB'
        },
        {
          icon: <FaCode />,
          title: 'Разработка',
          description: 'Реализуем проект с использованием современных технологий. Проводим регулярные демонстрации результатов и вносим корректировки.',
          color: '#99ABBA',
          lightColor: '#6C93DB'
        },
        {
          icon: <FaUserCheck />,
          title: 'Тестирование и оптимизация',
          description: 'Проводим всестороннее тестирование, выявляем и устраняем ошибки, оптимизируем производительность и пользовательский опыт.',
          color: '#CC94A3',
          lightColor: '#9B60D3'
        },
        {
          icon: <FaRocket />,
          title: 'Запуск и поддержка',
          description: 'Выполняем запуск проекта, обеспечиваем обучение и предоставляем техническую поддержку для дальнейшего развития и масштабирования.',
          color: '#FF7D8C',
          lightColor: '#7E22CE'
        }
      ]
    },
    en: {
      title: "How We Work",
      description: "Our development process is focused on results and transparency at every stage. We follow a proven methodology that ensures high quality and efficiency.",
      steps: [
        {
          icon: <FaLightbulb />,
          title: 'Idea and Consultation',
          description: 'We discuss your ideas, business goals, and project requirements. We provide a free consultation, define tasks, and create an action plan.',
          color: '#00F0FF',
          lightColor: '#1A56DB'
        },
        {
          icon: <FaSearch />,
          title: 'Research and Analytics',
          description: 'We analyze the market, competitors, and target audience. We define key success metrics and form technical specifications.',
          color: '#33D9E8',
          lightColor: '#3670DB'
        },
        {
          icon: <FaPencilRuler />,
          title: 'Design and Prototyping',
          description: 'We develop prototypes and design concepts, build user scenarios, and create a unique visual language for the project.',
          color: '#66C2D1',
          lightColor: '#5182DB'
        },
        {
          icon: <FaCode />,
          title: 'Development',
          description: 'We implement the project using modern technologies. We conduct regular demonstrations of results and make adjustments.',
          color: '#99ABBA',
          lightColor: '#6C93DB'
        },
        {
          icon: <FaUserCheck />,
          title: 'Testing and Optimization',
          description: 'We conduct comprehensive testing, identify and fix bugs, optimize performance and user experience.',
          color: '#CC94A3',
          lightColor: '#9B60D3'
        },
        {
          icon: <FaRocket />,
          title: 'Launch and Support',
          description: 'We launch the project, provide training, and offer technical support for further development and scaling.',
          color: '#FF7D8C',
          lightColor: '#7E22CE'
        }
      ]
    }
  };
  
  // Get translated text
  const getText = (key) => translations[currentLanguage]?.[key] || translations.ru[key];
  
  // Get current steps based on language
  const steps = getText('steps');
  
  // Intersection Observer для анимации при скролле
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  
  // Автоматическое переключение шагов
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [steps.length]);

  const nextStep = () => {
    setActiveStep((prev) => (prev + 1) % steps.length);
  };
  
  const prevStep = () => {
    setActiveStep((prev) => (prev === 0 ? steps.length - 1 : prev - 1));
  };

  return (
    <div 
      id="work-process"
      ref={sectionRef}
      className={`py-16 relative ${
        currentTheme === 'light' ? 'bg-white' : 'bg-[#080808]'
      }`}
    >
      <div className={`absolute top-0 left-0 w-full h-full bg-[url('https://readdy.ai/api/search-image?query=abstract%2520digital%2520workflow%2520background%2520with%2520dark%2520theme%252C%2520tech%2520process%2520visualization%2520concept&width=1600&height=900')] bg-cover bg-center ${
        currentTheme === 'light' ? 'opacity-2' : 'opacity-5'
      }`}></div>
      
      <div className="container mx-auto px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className={`bg-clip-text text-transparent bg-gradient-to-r ${
              currentTheme === 'light' 
                ? 'from-light-primary to-light-secondary' 
                : 'from-dark-primary to-dark-secondary'
            }`}>
              {getText('title')}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto ${
            currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            {getText('description')}
          </p>
        </div>
        
        {/* New Desktop View */}
        <div className="hidden md:block">
          <div className={`relative flex items-center justify-center mb-16 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          } transition-opacity duration-1000`}>
            {/* Circular progress indicator */}
            <div className="absolute w-72 h-72 rounded-full border-4 border-dashed border-opacity-40 
              flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2
              animate-slow-spin"
              style={{ 
                borderColor: currentTheme === 'light' ? 'rgba(26, 86, 219, 0.3)' : 'rgba(0, 240, 255, 0.3)' 
              }}
            ></div>
            
            {/* Main circular container */}
            <div className="w-64 h-64 rounded-full bg-opacity-10 flex items-center justify-center relative z-10"
              style={{ 
                backgroundColor: currentTheme === 'light' 
                  ? 'rgba(26, 86, 219, 0.1)' 
                  : 'rgba(0, 240, 255, 0.1)' 
              }}
            >
              {/* Central icon and number */}
              <div className={`w-48 h-48 rounded-full flex flex-col items-center justify-center transition-all duration-500
                ${currentTheme === 'light' 
                  ? 'bg-white shadow-lg' 
                  : 'bg-gradient-to-br from-[#0A0A0A] to-[#101010] border border-[#222]'
                }`}
              >
                <div className="text-5xl mb-3" style={{ 
                  color: currentTheme === 'light' 
                    ? steps[activeStep].lightColor 
                    : steps[activeStep].color 
                }}>
                  {steps[activeStep].icon}
                </div>
                <span className={`text-3xl font-bold ${
                  currentTheme === 'light' ? 'text-gray-800' : 'text-white'
                }`}>
                  {activeStep + 1}
                </span>
              </div>
            </div>
            
            {/* Radial step positions */}
            {steps.map((step, index) => {
              // Calculate position on the circle
              const angle = (index * (360 / steps.length)) * (Math.PI / 180);
              const radius = 150;
              const x = Math.cos(angle) * radius;
              const y = Math.sin(angle) * radius;
              
              return (
                <div 
                  key={index}
                  className={`absolute w-12 h-12 rounded-full cursor-pointer flex items-center justify-center
                    transition-all duration-300 hover:scale-110
                    ${index === activeStep 
                      ? currentTheme === 'light'
                        ? 'bg-gradient-to-r from-light-primary to-light-secondary shadow-lg shadow-light-secondary/20 scale-110' 
                        : 'bg-gradient-to-r from-dark-primary to-dark-secondary shadow-lg shadow-dark-secondary/20 scale-110'
                      : currentTheme === 'light'
                        ? 'bg-white border border-gray-200 shadow-sm'
                        : 'bg-[#111] border border-[#333]'
                    }`}
                  onClick={() => setActiveStep(index)}
                  style={{ 
                    transform: 'translate(-50%, -50%)',
                    left: `calc(50% + ${x}px)`, 
                    top: `calc(50% + ${y}px)`,
                  }}
                >
                  <span className={`text-sm font-bold ${
                    index === activeStep 
                      ? 'text-white' 
                      : currentTheme === 'light'
                        ? 'text-gray-700'
                        : 'text-gray-300'
                  }`}>{index + 1}</span>
                </div>
              );
            })}
            
            {/* Navigation buttons */}
            <button 
              className={`absolute -left-6 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300
                ${currentTheme === 'light'
                  ? 'bg-white border border-gray-200 text-gray-600 hover:border-light-primary hover:text-light-primary shadow-sm'
                  : 'bg-[#111] border border-[#333] text-white hover:border-dark-primary hover:text-dark-primary'
                }`}
              onClick={prevStep}
            >
              <FaArrowLeft />
            </button>
            
            <button 
              className={`absolute -right-6 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300
                ${currentTheme === 'light'
                  ? 'bg-white border border-gray-200 text-gray-600 hover:border-light-primary hover:text-light-primary shadow-sm'
                  : 'bg-[#111] border border-[#333] text-white hover:border-dark-primary hover:text-dark-primary'
                }`}
              onClick={nextStep}
            >
              <FaArrowRight />
            </button>
          </div>
          
          {/* Title and description of active step */}
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            key={activeStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className={`text-2xl font-bold mb-4 ${
              currentTheme === 'light' ? 'text-gray-800' : 'text-white'
            }`}>
              {steps[activeStep].title}
            </h3>
            <p className={`leading-relaxed ${
              currentTheme === 'light' ? 'text-gray-600' : 'text-gray-300'
            }`}>
              {steps[activeStep].description}
            </p>
          </motion.div>
          
          {/* Step indicators */}
          <div className="flex justify-center space-x-3 mt-10">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveStep(index)}
                className={`transition-all duration-300 ${
                  index === activeStep 
                    ? `w-10 h-3 rounded-full ${
                        currentTheme === 'light' 
                          ? 'bg-gradient-to-r from-light-primary to-light-secondary' 
                          : 'bg-gradient-to-r from-dark-primary to-dark-secondary'
                      }` 
                    : `w-3 h-3 rounded-full ${
                        currentTheme === 'light' ? 'bg-gray-200' : 'bg-[#222]'
                      }`
                }`}
                aria-label={`Перейти к шагу ${index + 1}`}
              />
            ))}
          </div>
        </div>
        
        {/* Mobile View - Keeping the existing one */}
        <div className="block md:hidden mt-12">
          <div className="space-y-8">
            {steps.map((step, index) => (
              <div 
                key={index}
                onClick={() => setActiveStep(index)}
                className={`flex items-start cursor-pointer transition-opacity duration-300 ${
                  index === activeStep ? 'opacity-100' : 'opacity-50 hover:opacity-75'
                }`}
              >
                <div 
                  className={`relative flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full mr-4 transition-all duration-300 ${
                    index <= activeStep 
                      ? currentTheme === 'light'
                        ? `bg-gradient-to-r from-light-primary to-light-secondary ${index === activeStep ? 'ring-2 ring-light-primary/30' : ''}` 
                        : `bg-gradient-to-r from-dark-primary to-dark-secondary ${index === activeStep ? 'ring-2 ring-dark-primary/30' : ''}`
                      : currentTheme === 'light'
                        ? 'bg-gray-200'
                        : 'bg-[#222]'
                  }`}
                >
                  <span className={`text-xs font-bold ${
                    index <= activeStep ? 'text-white' : currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'
                  }`}>{index + 1}</span>
                  
                  {/* Vertical line */}
                  {index < steps.length - 1 && (
                    <div 
                      className={`absolute top-full left-1/2 w-0.5 h-16 transform -translate-x-1/2 ${
                        index < activeStep 
                          ? currentTheme === 'light'
                            ? 'bg-gradient-to-b from-light-primary to-light-secondary' 
                            : 'bg-gradient-to-b from-dark-primary to-dark-secondary'
                          : currentTheme === 'light'
                            ? 'bg-gray-200'
                            : 'bg-[#222]'
                      }`}
                    ></div>
                  )}
                </div>
                
                <div className="pt-1">
                  <h4 className={`font-bold ${
                    index === activeStep 
                      ? currentTheme === 'light' ? 'text-gray-800' : 'text-white'
                      : currentTheme === 'light' ? 'text-gray-500' : 'text-gray-400'
                  } transition-colors duration-300`}>
                    {step.title}
                  </h4>
                  {index === activeStep && (
                    <motion.p 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`text-sm mt-2 ${
                        currentTheme === 'light' ? 'text-gray-600' : 'text-gray-400'
                      }`}
                    >
                      {step.description}
                    </motion.p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Add animation for rotating outer circle */}
      <style>{`
        @keyframes slow-spin {
          from {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }
        .animate-slow-spin {
          animation: slow-spin 60s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default WorkProcess; 